import { OrganizationIcon, PersonIcon, PlusIcon } from "@primer/octicons-react";
import { Link, useSearchParams } from "react-router-dom";
import { useMe } from "../api/hooks";
import { AvatarImage } from "../components/AvatarImage";
import { Empty, Loading, Page } from "../components/common";
import { Badge, Counter } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { SideNav, SideNavHead, SideNavLayout, Subhead, sideNavItem } from "../components/SettingsLayout";
import { TabLabel } from "../components/ui/tabs";
import { AccountPanel } from "./Account";

/** `/profile`: you, as a person: your organizations and your account and connections. Repositories live in organizations. */
export function Profile() {
  const me = useMe();
  const [params] = useSearchParams();
  if (me.isLoading) return <Page><Loading /></Page>;
  const user = me.data?.user;
  const orgs = me.data?.orgs ?? [];
  if (!user) {
    return (
      <Page>
        <Empty>You signed in with an access token, which has no user profile.</Empty>
      </Page>
    );
  }
  const tab = params.get("tab") === "orgs" ? "orgs" : "account";
  const tabs = [
    { id: "account", Icon: PersonIcon, label: "Account", count: 0, to: "/profile" },
    { id: "orgs", Icon: OrganizationIcon, label: "Organizations", count: orgs.length, to: "/profile?tab=orgs" },
  ];
  return (
    <Page>
      <SideNavLayout
        nav={
          <SideNav label="Profile">
            <SideNavHead icon={<AvatarImage src={user.avatar_url} name={user.name} size={32} />} title={user.display} subtitle={user.name} />
            {tabs.map((t) => (
              <Link key={t.id} to={t.to} aria-current={t.id === tab ? "page" : undefined} className={sideNavItem}>
                <t.Icon size={16} /> <TabLabel>{t.label}</TabLabel> {t.count ? <Counter className="ml-auto">{t.count}</Counter> : null}
              </Link>
            ))}
          </SideNav>
        }
      >
        {tab === "account" ? (
          <>
            <Subhead title="Account">{user.email ?? "Your sign-in and the accounts imports and remotes use."}</Subhead>
            <AccountPanel />
          </>
        ) : (
          <>
            <Subhead
              title="Organizations"
              actions={
                <Button asChild variant="primary">
                  <Link to="/orgs/new">
                    <PlusIcon /> New organization
                  </Link>
                </Button>
              }
            />
            <Card>
              {orgs.length ? (
                orgs.map((o) => (
                  <div key={o.id} className="flex items-center gap-3 border-b border-border-muted px-4 py-3 last:border-b-0">
                    <OrganizationIcon size={20} />
                    <div className="min-w-0 flex-1">
                      <Link to={`/orgs/${encodeURIComponent(o.name)}`} className="font-semibold">
                        {o.name}
                      </Link>
                      {o.description && <div className="truncate text-xs text-muted-foreground">{o.description}</div>}
                    </div>
                    {o.name === user.name.toLowerCase() && <Badge>personal</Badge>}
                    <Badge>{o.role}</Badge>
                  </div>
                ))
              ) : (
                <Empty icon={OrganizationIcon}>You are not in an organization yet.</Empty>
              )}
            </Card>
          </>
        )}
      </SideNavLayout>
    </Page>
  );
}
